import { appEnvironment, appName } from '@benzinga/utils';
import { MetaProps, OpenGraphType, Paywall } from './entities';
import { Metadata } from 'next';
import { extractSeoMeta, decodeDescription } from './utils';

export type MetaV2Type = MetaProps & { paywall?: Paywall };

export const metaV2 = async (props: MetaV2Type) => {
  const {
    author,
    canonical,
    description,
    hreflangAlternates,
    hrefLanguage,
    image,
    ogDescription,
    ogImage,
    ogImageHeight,
    ogImageWidth,
    ogTitle,
    ogType,
    robots,
    shortUrl,
    symbol,
    title,
    twitterCard,
    twitterDescription,
    twitterImage,
    twitterTitle,
  } = await extractSeoMeta(props);

  const meta: Metadata = {
    alternates: {
      canonical: canonical,
      languages: hreflangAlternates,
      types: {
        'application/rss+xml': 'http://feeds.benzinga.com/benzinga',
      },
      ...(hrefLanguage && hrefLanguage !== 'en' ? { hrefLang: { [hrefLanguage]: canonical } } : {}),
    },

    authors: [{ name: author }],

    description: description || '',

    icons: {
      icon: [
        { rel: 'apple-touch-icon', sizes: '180x180', url: '/next-assets/images/apple-touch-icon.png' },
        { rel: 'icon', sizes: '32x32', type: 'image/png', url: '/next-assets/images/favicon-32x32.png' },
        { rel: 'icon', sizes: '16x16', type: 'image/png', url: '/next-assets/images/favicon-16x16.png' },
      ],
      other: [
        ...(image ? [{ rel: 'image_src', url: image }] : []),
        { color: '#1177BA', rel: 'mask-icon', url: '/next-assets/images/safari-pinned-tab.svg' },
        { rel: 'manifest', url: '/next-assets/site.webmanifest' },
        { color: '#5bbad5', rel: 'mask-icon', url: '/next-assets/images/safari-pinned-tab.svg' },
      ],
    },

    manifest: '/manifest.json',

    openGraph: {
      siteName: 'Benzinga',
      ...(canonical && { url: canonical }),
      description: decodeDescription(ogDescription),
      images: ogImage ? [{ height: String(ogImageHeight), url: ogImage, width: String(ogImageWidth) }] : [],
      title: ogTitle,
      type: ogType as OpenGraphType,
    },
    other: {
      ...(symbol ? { ticker: symbol } : {}),
      copyright: 'Copyright Benzinga. All rights reserved.',
      'fb:app_id': '159483118580',
      'msapplication-TileColor': '#2d89ef',
      ...(canonical && { 'syndication-source': canonical }),
      'apple-itunes-app': 'app-id=688949481, app-argument=https://itunes.apple.com/us/app/id688949481',
      'apple-mobile-web-app-status-bar-style': 'black',
      'fb:admins': '648186192',
      'fo-verify': '101a0095-334d-4935-b87e-0e8fc771214e',
      'mobile-web-app-capable': 'yes',
      'msvalidate.01': 'B01B3489A33F83DDA4083E88C260097C',
      'og:image:type': 'image/jpeg',
      y_key: ['dbc639293c56bcb2', '84f3b1552db5dfbe'],
      ...(hrefLanguage === 'en' && { language: 'English' }),
      ...(shortUrl ? { shortlink: shortUrl.toString() } : {}),
    },
    robots: [robots, 'max-image-preview:large'].filter(Boolean).join(','),
    //themeColor: '#ffffff',
    title: title,
    twitter: {
      card: twitterCard || (appEnvironment().isApp(appName.india) ? 'summary_large_image' : 'summary'),
      description: decodeDescription(twitterDescription),
      images: twitterImage ? [twitterImage] : [],
      site: '@benzinga',
      title: twitterTitle,
    },
  };

  return meta;
};
